// Low pavement barriers (Phase 5): a horizontal bar across a pavement lane
// that must be slid under. Fairness: never within reaction distance of a
// jump obstacle (construction site) or another barrier in the same lane.

import * as THREE from 'three';
import { LANES, SPAWN_DISTANCE, BARRIER } from '@/config/tuning';
import type { Rng } from '@/core/rng';
import type { ConstructionSystem } from '@/entities/construction';

export interface LowBarrier {
  group: THREE.Group;
  laneX: number;
}

const postMat = new THREE.MeshLambertMaterial({ color: 0x8a2a2a });
const barMat = new THREE.MeshLambertMaterial({ color: 0xd94444 });
const stripeMat = new THREE.MeshLambertMaterial({ color: 0xffffff });

function buildBarrier(): THREE.Group {
  const group = new THREE.Group();
  const barY = (BARRIER.BAR_LOW + BARRIER.BAR_HIGH) / 2;
  for (const x of [-1.2, 1.2]) {
    const post = new THREE.Mesh(
      new THREE.CylinderGeometry(0.06, 0.06, BARRIER.BAR_HIGH, 8),
      postMat,
    );
    post.position.set(x, BARRIER.BAR_HIGH / 2, 0);
    post.castShadow = true;
    group.add(post);
  }
  const bar = new THREE.Mesh(new THREE.BoxGeometry(2.6, 0.16, 0.16), barMat);
  bar.position.set(0, barY, 0);
  bar.castShadow = true;
  group.add(bar);
  for (const x of [-0.8, 0, 0.8]) {
    const stripe = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.17, 0.17), stripeMat);
    stripe.position.set(x, barY, 0);
    group.add(stripe);
  }
  return group;
}

export class BarrierSystem {
  readonly barriers: LowBarrier[] = [];

  constructor(
    private readonly scene: THREE.Scene,
    private readonly rng: Rng,
  ) {}

  /** Spawn unless a jump obstacle sits too close in the same lane. */
  spawn(construction: ConstructionSystem): void {
    const lane: 0 | 3 = this.rng.next() < 0.5 ? 0 : 3;
    const laneX = LANES[lane];

    for (const site of construction.sites) {
      if (
        Math.abs(site.group.position.x - laneX) < 0.1 &&
        Math.abs(site.group.position.z - SPAWN_DISTANCE) <
          BARRIER.MIN_GAP_TO_JUMP_OBSTACLE + site.depth / 2
      ) {
        return; // Too close to a jump obstacle — skip silently
      }
    }
    for (const other of this.barriers) {
      if (
        Math.abs(other.laneX - laneX) < 0.1 &&
        Math.abs(other.group.position.z - SPAWN_DISTANCE) < BARRIER.MIN_GAP_TO_JUMP_OBSTACLE
      ) {
        return;
      }
    }

    const group = buildBarrier();
    group.position.set(laneX, 0.08, SPAWN_DISTANCE);
    this.scene.add(group);
    this.barriers.push({ group, laneX });
  }

  /** True if a barrier is near this lane position (spawn fairness for others). */
  isNear(x: number, z: number, gap: number): boolean {
    return this.barriers.some(
      (b) => Math.abs(b.laneX - x) < 0.1 && Math.abs(b.group.position.z - z) < gap,
    );
  }

  update(dt: number, gameSpeed: number): void {
    const dz = gameSpeed * dt * 60;
    for (let i = this.barriers.length - 1; i >= 0; i--) {
      const barrier = this.barriers[i];
      if (!barrier) continue;
      barrier.group.position.z += dz;
      if (barrier.group.position.z > 15) this.despawn(i);
    }
  }

  despawn(index: number): void {
    const barrier = this.barriers[index];
    if (!barrier) return;
    this.scene.remove(barrier.group);
    this.barriers.splice(index, 1);
  }

  clear(): void {
    for (let i = this.barriers.length - 1; i >= 0; i--) this.despawn(i);
  }
}
